import type { JSX, ReactNode, ComponentPropsWithoutRef, MouseEvent, ElementType } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { isNil, isNotNil, isNonEmptyString } from '@wistia/type-guards';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { Icon } from '../Icon';

const TagLabel = styled.a<{
  $colorScheme: ColorSchemeTypes;
  as: ElementType;
}>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  font-size: var(--wui-typography-label-4-size);
  font-weight: var(--wui-typography-label-4-weight);
  line-height: var(--wui-typography-label-4-line-height);
  border-radius: var(--wui-border-radius-01);
  display: flex;
  align-items: center;
  color: var(--wui-color-text);
  text-decoration: none;
  padding: var(--wui-space-01);
  gap: var(--wui-space-01);

  svg {
    height: 12px;
    width: 12px;
    flex: 0 0 12px;
  }

  &:focus-visible {
    outline: unset;
    box-shadow: inset 0 0 0 2px var(--wui-color-border-secondary);
  }

  &:not(:only-child) {
    padding-right: var(--wui-space-01);
  }

  &:is(a):hover {
    background: var(--wui-color-bg-surface-secondary-hover);
  }

  &:is(a):active {
    background: var(--wui-color-bg-surface-secondary-active);
  }
`;

const TagDivider = styled.div<{
  $colorScheme: ColorSchemeTypes;
}>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  border-left: 1px solid var(--wui-color-border);
  display: flex;
  height: 14px;
  width: 1px;
`;

const StyledRemoveButton = styled.button<{
  $colorScheme: ColorSchemeTypes;
}>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  all: unset;
  cursor: pointer;
  display: flex;
  height: 22px;
  width: 12px;
  align-items: center;
  padding: 0 var(--wui-space-01);
  border-radius: var(--wui-border-radius-01);

  svg path {
    fill: var(--wui-color-icon);
  }

  &:hover {
    background: var(--wui-color-bg-surface-secondary-hover);
  }

  &:active {
    background: var(--wui-color-bg-surface-secondary-active);
  }

  &:focus-visible {
    outline: unset;
    box-shadow: inset 0 0 0 2px var(--wui-color-border-secondary);
  }
`;

const StyledTag = styled.div<{
  $colorScheme: ColorSchemeTypes;
}>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  align-items: center;
  background-color: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-01);
  display: inline-flex;
  overflow: hidden;

  > * {
    transition: background var(--wui-duration-01) var(--wui-motion-ease);
  }
`;

type RemoveButtonProps = {
  onClickRemove?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  onClickRemoveLabel?: string | undefined;
  colorScheme: ColorSchemeTypes;
};

const RemoveButton = ({ onClickRemove, onClickRemoveLabel, colorScheme }: RemoveButtonProps) => {
  if (isNil(onClickRemove)) {
    return null;
  }

  if (isNil(onClickRemoveLabel)) {
    throw new Error(
      'for accessibility, onClickRemoveLabel must be provided if onClickRemove is provided',
    );
  }

  return (
    <>
      <TagDivider $colorScheme={colorScheme} />
      <StyledRemoveButton
        $colorScheme={colorScheme}
        onClick={onClickRemove}
        type="button"
      >
        <Icon
          size="sm"
          type="close"
        />
        <ScreenReaderOnly>{onClickRemoveLabel}</ScreenReaderOnly>
      </StyledRemoveButton>
    </>
  );
};

export type TagProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Valid URL for the `Tag` label to link to
   */
  href?: string;
  /**
   * Optional icon to display before the label
   */
  icon?: JSX.Element | undefined;
  /**
   * Text label for the `Tag`
   */
  label: ReactNode;
  /**
   * Callback function to be called when the `x` button is clicked
   */
  onClickRemove?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  /**
   * Accessible label for the `x` button (ie. "Remove tag")
   */
  onClickRemoveLabel?: string | undefined;
};

/**
 * A `Tag` is an optionally linked label, with an optional action button, to categorize content.
 */
export const Tag = forwardRef<HTMLDivElement, TagProps>(
  (
    { onClickRemove, colorScheme = 'inherit', href, icon, label, onClickRemoveLabel, ...props },
    ref,
  ): JSX.Element => {
    const hasIcon = isNotNil(icon);
    // if we get an href, we should render an anchor tag, otherwise a span
    const labelProps =
      isNotNil(href) && isNonEmptyString(href)
        ? { href, as: 'a' as const }
        : { as: 'span' as const };

    return (
      <StyledTag
        ref={ref}
        $colorScheme={colorScheme}
        {...props}
      >
        <TagLabel
          {...labelProps}
          $colorScheme={colorScheme}
        >
          {hasIcon ? icon : null}
          {label}
        </TagLabel>
        <RemoveButton
          colorScheme={colorScheme}
          onClickRemove={onClickRemove}
          onClickRemoveLabel={onClickRemoveLabel}
        />
      </StyledTag>
    );
  },
);

Tag.displayName = 'Tag';
